import { queryOptions } from "@tanstack/react-query";

import type { AssetKey } from "~/lib/functions/nova-assets";
import {
  getNovaAssetCollection,
  getNovaAssetDetail,
  getNovaAssets,
  getNovaContent,
  getNovaHeldWork,
  getNovaKnows,
} from "~/lib/functions/nova-assets";
import { novaHomeQueryKeys } from "~/lib/queries/novaHomeQueryKeys";

/**
 * The prefix every coach's asset rows share. Exported so a mutation that moves
 * those rows can invalidate them without knowing whose they are. The
 * workspace replay (`novaWorkspaceProjection`) refreshes it with the other
 * review surfaces.
 */
export const NOVA_ASSETS_QUERY_KEY = novaHomeQueryKeys.assets;

export function novaAssetsQueryOptions(userId: string | undefined) {
  return queryOptions({
    queryKey: [...NOVA_ASSETS_QUERY_KEY, userId],
    queryFn: () => getNovaAssets(),
    // Statuses move whenever the coach approves something, finishes a chat, or
    // their billing changes — all of which happen in the same session, so this
    // cannot sit on a long cache. Short enough to feel live, long enough that
    // navigating between the two asset surfaces does not re-read eight tables.
    staleTime: 15 * 1000,
    enabled: !!userId,
  });
}

/**
 * One asset's page, or with `itemId` one item's page. Keyed under the same
 * prefix, so every invalidation that refreshes the list refreshes the page
 * with it.
 */
export function novaAssetDetailQueryOptions(
  userId: string | undefined,
  assetKey: AssetKey,
  itemId?: string,
) {
  return queryOptions({
    queryKey: [
      ...NOVA_ASSETS_QUERY_KEY,
      userId,
      "detail",
      assetKey,
      ...(itemId === undefined ? [] : [itemId]),
    ],
    queryFn: () => getNovaAssetDetail({ data: { assetKey, itemId } }),
    staleTime: 15 * 1000,
    enabled: !!userId,
  });
}

/** One collection's items, under the same prefix as the list. */
export function novaAssetCollectionQueryOptions(
  userId: string | undefined,
  assetKey: AssetKey,
) {
  return queryOptions({
    queryKey: [...NOVA_ASSETS_QUERY_KEY, userId, "collection", assetKey],
    queryFn: () => getNovaAssetCollection({ data: { assetKey } }),
    staleTime: 15 * 1000,
    // Articles and testimonials are also edited outside NOVA, by editors that
    // invalidate only their own reads, so opening the page always reads again.
    refetchOnMount: "always",
    enabled: !!userId,
  });
}

/** Every content collection's items, for My content, under the same prefix. */
export function novaContentQueryOptions(userId: string | undefined) {
  return queryOptions({
    queryKey: [...NOVA_ASSETS_QUERY_KEY, userId, "content"],
    queryFn: () => getNovaContent(),
    staleTime: 15 * 1000,
    // The same reason as a collection's own read: articles and testimonials
    // are edited outside NOVA, by editors that invalidate only their own reads.
    refetchOnMount: "always",
    enabled: !!userId,
  });
}

/**
 * The work she holds whatever she can open, for the win-back panel, under the
 * same prefix so an approval anywhere refreshes it.
 */
export function novaHeldWorkQueryOptions(userId: string | undefined) {
  return queryOptions({
    queryKey: [...NOVA_ASSETS_QUERY_KEY, userId, "held-work"],
    queryFn: () => getNovaHeldWork(),
    staleTime: 60 * 1000,
    enabled: !!userId,
  });
}

/**
 * What NOVA knows, under the same prefix: approving an asset changes what a
 * record says, and every invalidation of the list reaches it.
 */
export function novaKnowsQueryOptions(userId: string | undefined) {
  return queryOptions({
    queryKey: [...NOVA_ASSETS_QUERY_KEY, userId, "knows"],
    queryFn: () => getNovaKnows(),
    staleTime: 15 * 1000,
    // The score and the profile change on their own pages, which invalidate
    // only their own reads, so opening the page always reads again.
    refetchOnMount: "always",
    enabled: !!userId,
  });
}
